// SPDX-License-Identifier: SEE LICENSE
//
// PillarTile — exact port of the dark "Live Diagnostic Engine - Battery"
// LEFT PILLARS tile. Compact 11×13 card, 22px index badge, title +
// 1-line summary + right-aligned latest value, then status pill + 20px
// sparkline in a row. Selected tile gets a teal halo + a CODE chip with
// a check icon on top.

import { Sparkline } from './Sparkline';
import { lastMetric, seriesFor } from '../lib/format';
import type { PillarMeta } from '../lib/pillars';
import type { StepKind, StepRecord, TelemetryFrame } from '../lib/types';

const PREVIEW_ALWAYS = new Set<StepKind>(['oled', 'vibro', 'magneto', 'selfemit', 'wipe'] as StepKind[]);
const SKIP_REASONS: Partial<Record<StepKind, string>> = {
  oled: 'SRDT 8×8 homography · camera+mirror · preview →',
  vibro: '30→110 Hz dual-sensor sweep · 60s flat surface · preview →',
  magneto: 'MSA hard-iron sphere fit · 60s figure-8 · preview →',
  selfemit: 'MSE chirplet · 7 components · 90s flat surface · preview →',
  wipe: 'K1→reset→K2 PoST · destructive · preview →',
};

const SENTINEL_HEADLINE_RE = /no run recorded|awaiting|no capture|verdict withheld/i;
function isSentinel(h: string | undefined | null): boolean {
  return !h || !h.trim() || SENTINEL_HEADLINE_RE.test(h);
}

// Pull the first finite numeric field from a step summary, trying each key in
// order. The backend persists a rich per-pillar `summary` for every terminal
// pillar even when no live telemetry frame carried the tile's primary metric
// key — so this lets a finished tile show its REAL measured value instead of a
// bare "—". Values are read straight from the signed summary; nothing fabricated.
function summaryNumber(
  summary: Record<string, unknown> | undefined,
  keys: string[],
): number | undefined {
  if (!summary) return undefined;
  for (const k of keys) {
    const v = summary[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
}

// Per-pillar fallback metric, derived from the real step summary, used when the
// live telemetry frames didn't carry the tile's primary liveMetrics[0] key.
// Returns a short pre-formatted string (with unit) — honest, real numbers only.
function metricFromSummary(
  kind: StepKind,
  summary: Record<string, unknown> | undefined,
): string | undefined {
  if (!summary) return undefined;
  const n = (keys: string[]) => summaryNumber(summary, keys);
  let v: number | undefined;
  switch (kind) {
    case 'battery_soh':
      v = n(['soh_pct', 'avg_soh_pct']);
      return v != null ? `${v.toFixed(1)}%` : undefined;
    case 'battery_forecast':
      v = n(['days_to_80pct']);
      if (v != null) return `${Math.round(v)}d`;
      v = n(['avg_soh_pct', 'soh_pct']);
      return v != null ? `${v.toFixed(1)}%` : undefined;
    case 'touch':
      v = n(['score', 'scoreServer', 'scoreClient']);
      return v != null ? `${Math.round(v)}` : undefined;
    case 'oled':
      v = n(['health_score']);
      if (v != null) return `${Math.round(v)}`;
      v = n(['uniformity_db']);
      return v != null ? `${v.toFixed(1)}` : undefined;
    case 'vibro':
      v = n(['q_factor', 'qFactor']);
      if (v != null) return v.toFixed(1);
      v = n(['resonance_peak_hz', 'resonancePeakHz']);
      return v != null ? `${Math.round(v)}Hz` : undefined;
    case 'magneto':
      v = n(['mahalanobisServer', 'mahalanobisClient']);
      if (v != null) return v.toFixed(1);
      v = n(['earthMagnitudeUt']);
      return v != null ? `${v.toFixed(1)}µT` : undefined;
    case 'selfemit':
      v = n(['composite_score']);
      return v != null ? v.toFixed(2) : undefined;
    case 'ultrasonic':
      v = n(['in_band_snr_db', 'snr_db']);
      return v != null ? `${v.toFixed(0)}dB` : undefined;
    case 'drop':
      v = n(['new_events_during_window']);
      if (v != null) return `${Math.round(v)} ev`;
      v = n(['merkle_height_final']);
      return v != null ? `h${Math.round(v)}` : undefined;
    case 'acta':
      v = n(['coverage_pct']);
      if (v != null) return `${Math.round(v)}%`;
      v = n(['probes_passed']);
      return v != null ? `${Math.round(v)}` : undefined;
    case 'thermal':
      v = n(['peak_temp_c', 'baseline_temp_c']);
      return v != null ? `${v.toFixed(1)}°C` : undefined;
    default:
      return undefined;
  }
}

export function PillarTile({
  meta,
  step,
  frames,
  selected,
  onSelect,
}: {
  meta: PillarMeta;
  step: StepRecord | undefined;
  frames: TelemetryFrame[];
  selected: boolean;
  onSelect: () => void;
}) {
  const status = step?.status ?? 'pending';
  const running = status === 'active';
  const skipped = status === 'skipped';
  const terminal = status === 'passed' || status === 'concern' || status === 'failed' || skipped;
  const previewMode = PREVIEW_ALWAYS.has(meta.kind);
  const primaryKey = meta.liveMetrics[0] ?? '';
  // Honesty gate: surface the measured VALUE only once the pillar is
  // terminal. While pending/active the tile shows a live frame-count + an
  // "arming…" sparkline instead of the raw mid-capture reading, so a running
  // pillar never displays a number that looks like a finished result.
  const series = terminal ? seriesFor(frames, primaryKey) : [];
  const latest = terminal ? lastMetric(frames, primaryKey) : null;
  const rawHeadline = step?.summary?.headline as string | undefined;
  const skipReason = SKIP_REASONS[meta.kind];

  // Right-aligned value. Honesty gate: only show a measured value once
  // terminal. Prefer the live-frame reading; if the frames never carried this
  // tile's primary metric key (common on a reopened/finalized session where
  // only a subset of telemetry was persisted), fall back to the REAL value from
  // the signed step summary. A terminal pillar with a genuine verdict but no
  // recoverable number shows an honest verdict label — never a bare "—".
  const summary = step?.summary as Record<string, unknown> | undefined;
  const verdictLabel =
    status === 'passed' ? 'Verified' : status === 'concern' ? 'Concern' : status === 'failed' ? 'Failed' : null;
  let metricText: string;
  if (running) {
    metricText = `${frames.length}f`;
  } else if (typeof latest === 'number') {
    metricText = latest.toFixed(1);
  } else if (typeof latest === 'string') {
    metricText = latest;
  } else if (terminal && !skipped) {
    metricText = metricFromSummary(meta.kind, summary) ?? verdictLabel ?? '—';
  } else if (skipped) {
    metricText = 'Preview';
  } else {
    metricText = '—';
  }

  const headline =
    previewMode && skipReason
      ? skipReason
      : skipped && skipReason
        ? skipReason
        : !isSentinel(rawHeadline)
          ? (rawHeadline as string)
          : running
            ? 'Capturing…'
            : terminal
              ? frames.length === 0
                ? 'No telemetry · verdict withheld'
                : `Partial capture · ${frames.length} frames`
              : meta.tagline;

  // Sentinel-pass is rendered as concern in the tile (last-line defence).
  const displayStatus =
    terminal && isSentinel(rawHeadline) && status === 'passed' ? 'concern' : status;

  const isPass = displayStatus === 'passed';
  const isConcern = displayStatus === 'concern';
  const isFail = displayStatus === 'failed';

  const pillColor = isPass ? '#34d399' : isConcern ? '#f5bf45' : isFail ? '#f06a5e' : running ? '#15ddc9' : '#8aa0a6';
  const pillBg = isPass
    ? 'rgba(52,211,153,0.06)'
    : isConcern
      ? 'rgba(245,191,69,0.06)'
      : isFail
        ? 'rgba(240,106,94,0.06)'
        : running
          ? 'rgba(20,222,205,0.08)'
          : 'rgba(120,160,160,0.05)';
  const pillBorder = isPass
    ? 'rgba(52,211,153,0.22)'
    : isConcern
      ? 'rgba(245,191,69,0.22)'
      : isFail
        ? 'rgba(240,106,94,0.22)'
        : running
          ? 'rgba(20,222,205,0.3)'
          : 'rgba(120,160,160,0.16)';

  return (
    <button
      onClick={onSelect}
      style={{
        position: 'relative',
        textAlign: 'left',
        width: '100%',
        border: selected ? '1px solid rgba(20,222,205,0.65)' : '1px solid rgba(120,160,160,0.13)',
        background: selected ? 'rgba(20,222,205,0.05)' : 'rgba(255,255,255,0.012)',
        borderRadius: 11,
        padding: '11px 13px',
        boxShadow: selected
          ? '0 0 0 1px rgba(20,222,205,0.18), 0 0 26px -8px rgba(20,222,205,0.55)'
          : 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: '#cfe0e0',
        transition: 'border-color 200ms, box-shadow 200ms',
      }}
      className={running ? 'animate-livepulse' : ''}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            border: selected ? '1px solid rgba(20,222,205,0.5)' : '1px solid rgba(120,160,160,0.22)',
            background: selected ? 'rgba(20,222,205,0.1)' : 'transparent',
            color: selected ? '#15ddc9' : '#8aa0a6',
            fontSize: 10.5,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {meta.index}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 9.5,
                letterSpacing: 1,
                color: '#15ddc9',
                fontWeight: 600,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {meta.code}
            </div>
          ) : (
            <div style={{ fontSize: 9.5, letterSpacing: 1, color: '#6f878e', fontWeight: 600 }}>{meta.code}</div>
          )}
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f4faf9', marginTop: 4 }}>{meta.name}</div>
          <div
            style={{
              fontSize: 11,
              color: '#5d7178',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {headline}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div
            className="lde-mono"
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: '#e8f2f1',
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {metricText}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginTop: 9,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: pillBg,
            border: `1px solid ${pillBorder}`,
            color: pillColor,
            fontSize: 10.5,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {isPass ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : isConcern ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          ) : isFail ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : running ? (
            <span className="animate-livepulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#15ddc9' }} />
          ) : null}
          {isPass ? 'Passed' : isConcern ? 'Concern' : isFail ? 'Failed' : running ? 'Live' : skipped ? 'Preview' : 'Pending'}
        </span>

        {previewMode || skipped ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                fontSize: 9,
                letterSpacing: 0.8,
                fontWeight: 700,
                color: '#f5bf45',
                border: '1px solid rgba(245,191,69,0.4)',
                padding: '1px 6px',
                borderRadius: 5,
              }}
            >
              PREVIEW
            </span>
            <span style={{ fontSize: 10, color: '#5d7178', fontStyle: 'italic' }}>method-paper viz →</span>
          </span>
        ) : (
          <Sparkline
            data={series}
            color={selected ? '#15ddc9' : 'rgba(20,222,205,0.55)'}
            width={120}
            height={20}
            emptyText={running ? 'arming…' : null}
          />
        )}
      </div>
    </button>
  );
}
